/* NICECNC collection fitment engine — clean rebuild.
 * One state owner for Make -> Model -> Submodel -> Universal -> Year -> Part Type.
 * Shopify/Focal remains responsible for drawer UI, Availability, Price and product cards.
 */
(() => {
  'use strict';

  const VERSION = 'v60-maverick-x3-canonical';
  const P = {
    make: '_nc_make', model: '_nc_model', sub: '_nc_submodel',
    year: '_nc_year', part: '_nc_part', universal: '_nc_universal', other: '_nc_other'
  };
  const NATIVE_AVAIL = 'filter.v.availability';
  const PRICE_MIN = 'filter.v.price.gte';
  const PRICE_MAX = 'filter.v.price.lte';
  const SPECIAL_PARTS = new Set(['周边', '补款单', 'merchandise', 'payment adjustment']);
  const UY = 'Universal Year';
  const CACHE_TTL = 10 * 60 * 1000;
  const indexCache = new Map();
  const stateCache = new WeakMap();
  const mappingSubCache = new Map();
  const CARD_PAGE_SIZE = 250;
  const INDEX_CONCURRENCY = 6;
  const CARD_CONCURRENCY = 4;
  const D = window.NICECNCFitmentData || {};
  const FITMENT = D.FITMENT || {};
  const POLICY = window.NICECNCFacetPolicy || {};
  const makePriority = new Map((D.MAKE_PRIORITY || []).map((v, i) => [v, i]));
  const modelOwnerMap=new Map(), subOwnerMap=new Map(), subOwnerFallbackCache=new Map();

  const arr = v => Array.isArray(v) ? v.filter(Boolean) : v ? [v] : [];
  const norm = v => String(v || '').normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '');
  const words = v => String(v || '').normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
  const uniq = values => [...new Map(values.filter(Boolean).map(v => [norm(v), v])).values()];
  const natural = (a, b) => String(a).localeCompare(String(b), undefined, {numeric:true, sensitivity:'base'});
  const canonical = (values, value) => values.find(v => norm(v) === norm(value)) || value;
  const isUniversalValue = v => norm(v) === 'universal';
  const isUniversalYear = v => norm(v) === norm(UY);
  const isSpecialPart = v => SPECIAL_PARTS.has(String(v || '').trim()) || SPECIAL_PARTS.has(String(v || '').trim().toLowerCase());
  const contains = (parent, child) => POLICY.contains ? POLICY.contains(parent, child, '') : norm(parent) === norm(child);
  const makeAliasMap = new Map(Object.entries(D.MAKE_ALIASES || {}).map(([k,v]) => [norm(k), v]));
  const subAliasMap = new Map(Object.entries(D.SUBMODEL_ALIASES || {}).map(([k,v]) => [norm(k), v]));
  const subExpansionMap = new Map(Object.entries(D.SUBMODEL_EXPANSIONS || {}).map(([k,v]) => [norm(k), arr(v)]));
  const canonicalMakeValue = value => makeAliasMap.get(norm(value)) || value;
  function normalizedSubValues(value) {
    const expanded = subExpansionMap.get(norm(value));
    if (expanded?.length) return uniq(expanded.map(v => subAliasMap.get(norm(v)) || v));
    return [subAliasMap.get(norm(value)) || value].filter(Boolean);
  }
  function canonicalBranchModel(make, model, sub) {
    const makeKey=norm(make), modelKey=norm(model);
    const current=(modelOwnerMap.get(modelKey)||[]).find(o=>norm(o.make)===makeKey);
    const owners=(subOwnerMap.get(norm(sub))||[]).filter(o=>norm(o.make)===makeKey);
    if(current&&owners.some(o=>norm(o.model)===norm(current.model)))return current.model;
    if(owners.length===1)return owners[0].model;
    return current?.model||model;
  }
  for(const [mk,models] of Object.entries(FITMENT)){
    if(isUniversalValue(mk))continue;
    for(const [model,subs] of Object.entries(models||{})){
      const mkModel={make:mk,model};
      const mkey=norm(model);if(!modelOwnerMap.has(mkey))modelOwnerMap.set(mkey,[]);modelOwnerMap.get(mkey).push(mkModel);
      for(const sub of Object.keys(subs||{})){
        if(isUniversalValue(sub))continue;
        const skey=norm(sub);if(!subOwnerMap.has(skey))subOwnerMap.set(skey,[]);subOwnerMap.get(skey).push(mkModel);
      }
    }
  }

  function cleanUrl(input, {keepPage=true, keepNative=true} = {}) {
    const u = new URL(input, location.href);
    ['section_id','view','_nc_refresh'].forEach(k => u.searchParams.delete(k));
    if (!keepPage) u.searchParams.delete('page');
    if (!keepNative) {
      [...u.searchParams.keys()].forEach(k => {
        if (k.startsWith('filter.') || Object.values(P).includes(k)) u.searchParams.delete(k);
      });
    }
    return u;
  }
  function collectionBase(input) {
    const u = cleanUrl(input, {keepPage:false, keepNative:false});
    const sort = new URL(input, location.href).searchParams.get('sort_by');
    if (sort) u.searchParams.set('sort_by', sort);
    return u;
  }
  async function fetchText(url, {fresh=false, timeout=30000} = {}) {
    const u = new URL(url, location.href);
    if (fresh) u.searchParams.set('_nc_refresh', String(Date.now()));
    const c = new AbortController();
    const t = setTimeout(() => c.abort(), timeout);
    try {
      const r = await fetch(u, {credentials:'same-origin', cache:fresh?'no-store':'default', signal:c.signal});
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      return await r.text();
    } finally { clearTimeout(t); }
  }
  function parse(html) { return new DOMParser().parseFromString(html, 'text/html'); }

  function parseFitmentBranches(rawValue) {
    const rows=[];
    const pushBranch=(make,model,sub,year='')=>{
      make=String(canonicalMakeValue(make)||'').trim(); model=String(model||'').trim(); year=String(year||'').trim();
      if(!make||!model||!sub)return;
      for(const normalizedSub of normalizedSubValues(String(sub).trim())) if(normalizedSub) rows.push({make,model:canonicalBranchModel(make,model,normalizedSub),sub:normalizedSub,year});
    };
    const pushValue=value=>{
      if (!value) return;
      if (Array.isArray(value)) { value.forEach(pushValue); return; }
      if (typeof value === 'object') {
        pushBranch(value.make||value.Make||'', value.model||value.Model||'', value.submodel||value.Submodel||value.sub||'', value.year||value.Year||value.years||'');
        return;
      }
      String(value).split(/\r?\n+/).forEach(line=>{
        const parts=line.split('|').map(v=>v.trim());
        if(parts.length>=3) pushBranch(parts[0],parts[1],parts[2],parts.slice(3).join('|'));
      });
    };
    pushValue(rawValue);
    const seen=new Set();
    return rows.filter(b=>{const key=`${norm(b.make)}::${norm(b.model)}::${norm(b.sub)}::${norm(b.year)}`;if(seen.has(key))return false;seen.add(key);return true;});
  }
  function normalizeRawProduct(raw) {
    return {
      ...raw,
      make: uniq(arr(raw.make).map(canonicalMakeValue)), model: uniq(arr(raw.model)), submodel: uniq(arr(raw.submodel).flatMap(normalizedSubValues)),
      year: uniq(arr(raw.year)), part_type: uniq(arr(raw.part_type)), prices: arr(raw.prices).map(Number).filter(Number.isFinite),
      fitment_branches: parseFitmentBranches(raw.fitment_key)
    };
  }

  async function loadIndex(input = location.href) {
    const base = collectionBase(input);
    const key = base.href;
    const old = indexCache.get(key);
    if (old && Date.now() - old.time < CACHE_TTL) return old.promise;
    const item = {time:Date.now()};
    item.promise = (async () => {
      const pageUrl = page => { const u = new URL(base); u.searchParams.set('view','nicecnc-audit'); u.searchParams.set('page', String(page)); return u; };
      const first = JSON.parse(await fetchText(pageUrl(1)));
      if (!Array.isArray(first.products) || !Number.isInteger(first.total)) throw new Error('Invalid audit view');
      const total = first.total;
      const totalPages = Math.max(1, Math.ceil(total / 250));
      const pageData = new Array(totalPages);
      pageData[0] = first.products;
      const pending = [];
      for (let page=2; page<=totalPages; page++) pending.push(page);
      await Promise.all(Array.from({length:Math.min(INDEX_CONCURRENCY,pending.length)}, async () => {
        while (pending.length) {
          const page = pending.shift();
          const data = JSON.parse(await fetchText(pageUrl(page)));
          if (!Array.isArray(data.products) || data.total !== total) throw new Error('Collection changed during audit');
          pageData[page-1] = data.products;
        }
      }));
      const products = [], seen = new Set();
      for (const rows of pageData) for (const raw of rows || []) {
        if (!raw.handle || seen.has(raw.handle)) continue;
        seen.add(raw.handle); products.push(normalizeRawProduct(raw));
      }
      return finalizeIndex({products, total, base});
    })();
    indexCache.set(key, item);
    item.promise.catch(() => { if (indexCache.get(key) === item) indexCache.delete(key); });
    return item.promise;
  }


  function currentHandle(root) {
    return root?.dataset?.nicecncCollectionHandle || location.pathname.match(/\/collections\/([^/?#]+)/)?.[1] || '';
  }
  // This section is optional and local to each collection JSON template.
  // Never infer a fixed Collection from the template name or preview title.
  function templateScope() {
    const source=document.querySelector('script[data-nicecnc-fitment-template]');
    if(!source)return null;
    try {
      const config=JSON.parse(source.textContent);
      // Theme block JSON fragments contain only selected values. Static schema
      // choices never become storefront filters unless deliberately selected.
      const chosen={make:[],model:[],modelAll:[],submodel:[],submodelPaths:[]};
      for(const node of document.querySelectorAll('script[data-nicecnc-fitment-include]')){
        const type=node.dataset.nicecncFitmentInclude;
        if(!Object.prototype.hasOwnProperty.call(chosen,type))continue;
        try{chosen[type].push(...arr(JSON.parse(node.textContent)).filter(v=>typeof v==='string'&&v));}catch(_){}
      }
      const hasSelections=[chosen.make,chosen.model,chosen.submodel,chosen.submodelPaths].some(values=>values.length);
      // Explicit Include blocks take precedence over the section's old single-choice fields.
      if(hasSelections){
        config.brands=uniq(chosen.make);
        config.modelPaths=uniq(chosen.model);
        config.allowedNumberTokens=[];
        config.submodels=uniq(chosen.submodel);
      }
      if(chosen.modelAll.length)config.modelPaths=[];
      const subPaths=uniq(chosen.submodelPaths);
      if(subPaths.length){
        config.submodels=uniq(subPaths.map(path=>path.split('::').slice(2).join('::')));
        if(!arr(config.modelPaths).length)config.modelPaths=uniq(subPaths.map(path=>path.split('::').slice(0,2).join('::')));
      }else if(chosen.submodel.length)config.submodels=uniq(chosen.submodel);
      if(!['fitment','sportster'].includes(config?.pilot)||!['make','model','submodel','auto'].includes(config.startAt))return null;
      const allMakes=Object.keys(FITMENT).filter(m=>!isUniversalValue(m));
      const brands=uniq(arr(config.brands).filter(v=>typeof v==='string').map(v=>canonical(allMakes,v)).filter(v=>allMakes.some(m=>norm(m)===norm(v))));
      const modelChoices=arr(config.modelPaths||config.models).filter(v=>typeof v==='string');
      const modelPaths=[];
      for(const value of modelChoices){
        const separator=value.indexOf('::');
        const owners=separator>=0?[{make:value.slice(0,separator),model:value.slice(separator+2)}]:brands.flatMap(make=>Object.keys(FITMENT[make]||{}).filter(model=>norm(model)===norm(value)).map(model=>({make,model})));
        for(const owner of owners){
          const make=canonical(allMakes,owner.make);
          const model=canonical(Object.keys(FITMENT[make]||{}),owner.model);
          if(FITMENT[make]&&Object.keys(FITMENT[make]).some(v=>norm(v)===norm(model)))modelPaths.push({make,model});
        }
      }
      const selectedSubs=uniq(arr(config.submodels).filter(v=>typeof v==='string'));
      // Submodel-only configuration should also work: each selectable submodel
      // belongs to a known Make / Model in the existing dictionary.
      if(!brands.length&&!modelPaths.length&&selectedSubs.length){
        for(const sub of selectedSubs)modelPaths.push(...(subOwnerMap.get(norm(sub))||[]));
      }
      const paths=[...new Map(modelPaths.map(x=>[`${norm(x.make)}::${norm(x.model)}`,x])).values()];
      // When a Model is chosen but Make is left automatic, derive Make from the
      // chosen Model paths. A mismatched explicit Make/Model is not silently widened.
      const makes=brands.length?brands:uniq(paths.map(x=>x.make));
      const validPaths=paths.filter(x=>!brands.length||brands.some(v=>norm(v)===norm(x.make)));
      if(paths.length&&!validPaths.length)return null;
      const models=uniq(validPaths.map(x=>x.model));
      const subs=selectedSubs.filter(sub=>{
        const owners=validPaths.length?validPaths:makes.flatMap(make=>Object.keys(FITMENT[make]||{}).map(model=>({make,model})));
        return owners.some(({make,model})=>Object.keys(FITMENT[make]?.[model]||{}).some(v=>norm(v)===norm(sub)));
      });
      const series=arr(config.allowedNumberTokens).filter(n=>n===883||n===1200);
      const rule={keepSubmodelVisible:true};
      if(makes.length)rule.brands=makes;
      if(models.length){rule.models=models;rule.modelPaths=validPaths.map(x=>`${x.make}::${x.model}`);}
      if(subs.length){rule.submodels=subs;rule.closedSubmodels=true;}
      else if(series.length&&validPaths.length===1&&norm(validPaths[0].make)===norm('Harley-Davidson')&&norm(validPaths[0].model)===norm('Sportster'))rule.allowedNumberTokens=series;
      if(config.startAt!=='auto')rule.startAt=config.startAt;
      if(config.startAt==='make')rule.showMake=true;
      if(config.startAt==='make'||config.startAt==='model')rule.showModel=true;
      return Object.keys(rule).length>1?rule:null;
    } catch (_) {return null;}
  }
  // Existing maintained routes always win. For newly created Collections, allow a
  // JSON Collection metafield to override the conservative name-based fallback.
  function configuredScope(root) {
    const raw=root?.dataset?.nicecncFitmentFilterConfig;
    if(!raw)return null;
    try {
      let config=JSON.parse(raw);
      if(typeof config==='string')config=JSON.parse(config); // text or JSON metafield
      if(!config||typeof config!=='object'||Array.isArray(config))return null;
      const keys=['brands','models','submodels','startAt','showModel','allowedNumbers',
        'allowedNumberTokens','minNumber','maxNumber','submodelSuffix',
        'visibleSubmodelContainsAny','closedSubmodels','preservePolicySubmodels',
        'prioritySubmodels','numericFirst'];
      const rule={};
      for(const key of keys)if(Object.prototype.hasOwnProperty.call(config,key))rule[key]=config[key];
      if(rule.startAt&&!['make','model','submodel'].includes(rule.startAt))return null;
      return Object.keys(rule).length?rule:null;
    } catch (_) { return null; }
  }
  // Collection-native metafields: simple admin controls without handwritten JSON.
  function adminScope(root) {
    const data=root?.dataset||{};
    const field=name=>{try{return JSON.parse(data[name]||'null');}catch(_){return null;}};
    const values=name=>{
      const value=field(name);
      return Array.isArray(value)?uniq(value.filter(v=>typeof v==='string').map(v=>v.trim()).filter(Boolean)):[];
    };
    const startAt=field('nicecncFitmentStartAt');
    const brands=values('nicecncFitmentMakes'),models=values('nicecncFitmentModels');
    const submodels=values('nicecncFitmentSubmodels');
    const tokens=values('nicecncFitmentNumberTokens').map(Number).filter(n=>Number.isInteger(n)&&n>=0);
    const rule={};
    if(['make','model','submodel'].includes(startAt)){
      rule.startAt=startAt;
      if(startAt==='make')rule.showMake=true;
      if(startAt!=='submodel')rule.showModel=true;
    }
    if(brands.length)rule.brands=brands;
    if(models.length)rule.models=models;
    if(submodels.length)rule.submodels=submodels;
    if(tokens.length)rule.allowedNumberTokens=tokens;
    return Object.keys(rule).length?rule:null;
  }
  function inferredScope(root) {
    const name=words(root?.dataset?.nicecncCollectionTitle||currentHandle(root).replace(/-/g,' '));
    if(!name)return null;
    const phrase=` ${name} `;
    const makes=Object.keys(FITMENT).filter(make=>!isUniversalValue(make)&&phrase.includes(` ${words(make)} `));
    const candidates=[];
    for(const [make,models] of Object.entries(FITMENT)) {
      if(isUniversalValue(make)||(makes.length&&!makes.includes(make)))continue;
      for(const model of Object.keys(models||{})) {
        const term=words(model);
        if(term.length>=3&&phrase.includes(` ${term} `))candidates.push({make,model,term});
      }
    }
    if(candidates.length) {
      const longest=Math.max(...candidates.map(v=>v.term.length));
      const matches=candidates.filter(v=>v.term.length===longest);
      if(matches.length!==1)return makes.length===1?{brands:makes,startAt:'model',showModel:true}:null;
      const {make,model}=matches[0];
      const rule={brands:[make],models:[model],startAt:'submodel'};
      // A split e.g. "Sportster 1200" / "Sportster 883" names a numerical
      // subfamily within one Model. Match the full numeric token, including XL1200C.
      const subs=Object.keys(FITMENT[make][model]||{});
      const variants=new Set(subs.flatMap(s=>s.match(/\d{2,4}/g)||[]));
      const named=(name.match(/\d{2,4}/g)||[]).filter(n=>!model.match(/\d{2,4}/g)?.includes(n)&&variants.has(n));
      if(variants.size>1&&new Set(named).size===1)rule.allowedNumberTokens=[Number(named[0])];
      return rule;
    }
    return makes.length===1?{brands:makes,startAt:'model',showModel:true}:null;
  }
  function overrideFor(root) {
    const manual=templateScope();
    if(manual)return manual;
    const aliases = D.COLLECTION_HANDLE_ALIASES || {};
    const routes = D.COLLECTION_SCOPE_ROUTES || {};
    const rules = D.COLLECTION_FACET_OVERRIDES || {};
    const key = currentHandle(root);
    const canonicalKey = aliases[key] || key;
    const route = routes[key] || routes[canonicalKey] || routes[key.replace(/-canada$/i,'')] || routes[canonicalKey.replace(/-canada$/i,'')] || null;
    const maintained = rules[canonicalKey] || rules[canonicalKey.replace(/-canada$/i,'')] || rules[key] || null;
    if(route && maintained) return {...route, ...maintained};
    if(maintained || route)return maintained || route;
    const base=configuredScope(root)||inferredScope(root),admin=adminScope(root);
    if(!admin)return base;
    const rule={...(base||{})};
    // Explicit range changes replace an inferred narrower range, not intersect it.
    if(admin.brands&&JSON.stringify(admin.brands)!==JSON.stringify(rule.brands)){
      delete rule.models;delete rule.submodels;delete rule.allowedNumberTokens;
    }else if(admin.models&&JSON.stringify(admin.models)!==JSON.stringify(rule.models)){
      delete rule.submodels;delete rule.allowedNumberTokens;
    }
    if(admin.submodels)delete rule.allowedNumberTokens;
    if(!admin.startAt){
      if(admin.submodels)admin.startAt='submodel';
      else if(admin.models)admin.startAt=admin.models.length>1?'model':'submodel';
      else if(admin.brands)admin.startAt=admin.brands.length>1?'make':'model';
      if(admin.startAt==='make')admin.showMake=true;
      if(admin.startAt==='make'||admin.startAt==='model')admin.showModel=true;
    }
    return {...rule,...admin};
  }
  function firstNumber(value){const m=String(value||'').match(/(?:^|\D)(\d{2,4})(?=\D|$)/);return m?Number(m[1]):NaN;}
  function ruleAllowsNumber(value,rule){
    if(!rule)return true;
    if(Array.isArray(rule.allowedNumberTokens)&&rule.allowedNumberTokens.length){
      const found=String(value||'').match(/\d{2,4}/g)||[];
      if(!rule.allowedNumberTokens.some(n=>found.includes(String(n))))return false;
    }
    const allowed=Array.isArray(rule.allowedNumbers)?rule.allowedNumbers.filter(Number.isFinite):[];
    const hasMin=Number.isFinite(rule.minNumber),hasMax=Number.isFinite(rule.maxNumber);
    if(!allowed.length&&!hasMin&&!hasMax)return true;
    const n=firstNumber(value);if(!Number.isFinite(n))return false;
    if(allowed.length&&!allowed.includes(n))return false;
    return (!hasMin||n>=rule.minNumber)&&(!hasMax||n<=rule.maxNumber);
  }
  function ruleAllowsMake(value,rule){return !rule?.brands?.length||rule.brands.some(v=>norm(v)===norm(value));}
  function ruleAllowsModel(value,rule,make){return (!rule?.models?.length||rule.models.some(v=>norm(v)===norm(value)))&&(!make||!rule?.modelPaths?.length||rule.modelPaths.some(v=>norm(v)===norm(`${make}::${value}`)));}
  function ruleAllowsSub(value,rule){
    if(!rule)return true;
    if(!ruleAllowsNumber(value,rule))return false;
    if(rule.visibleSubmodelContainsAny?.length){
      const actual=words(value);
      if(!rule.visibleSubmodelContainsAny.some(token=>actual.includes(words(token))))return false;
    }
    if(rule.submodelSuffix){
      const actual=String(value||'').trim().toLowerCase().replace(/\s+/g,' ');
      const suffix=String(rule.submodelSuffix||'').trim().toLowerCase().replace(/\s+/g,' ');
      if(actual!==suffix&&!actual.endsWith(` ${suffix}`))return false;
    }
    if(rule.preservePolicySubmodels===true||!rule.submodels?.length)return true;
    if(rule.closedSubmodels===true)return rule.submodels.some(v=>norm(v)===norm(value));
    return rule.submodels.some(v=>norm(v)===norm(value)||contains(v,value));
  }
  function ruleScopeAllowsSub(value,rule){
    if(!rule)return true;
    if(!ruleAllowsNumber(value,rule))return false;
    if(rule.submodelSuffix){
      const actual=String(value||'').trim().toLowerCase().replace(/\s+/g,' ');
      const suffix=String(rule.submodelSuffix||'').trim().toLowerCase().replace(/\s+/g,' ');
      if(actual!==suffix&&!actual.endsWith(` ${suffix}`))return false;
    }
    if(rule.preservePolicySubmodels===true||!rule.submodels?.length)return true;
    // closedSubmodels controls what shoppers SEE, not which real descendants
    // prove Collection membership. A visible parent row still owns its descendants.
    return rule.submodels.some(v=>norm(v)===norm(value)||contains(v,value));
  }
  function inferScope(root, index, h) {
    const rule = overrideFor(root);
    const allMakes = index._liveMakes || uniq(index.products.flatMap(effectiveMakes));
    const liveMakes = allMakes.filter(v=>ruleAllowsMake(v,rule));
    const allModels = index._liveModels || uniq(index.products.flatMap(p => effectiveMakes(p).flatMap(mk=>effectiveModels(p,mk))));
    const liveModels = allModels.filter(v=>ruleAllowsModel(v,rule));
    const allSubs = index._liveSubs || uniq(index.products.flatMap(p => effectiveMakes(p).flatMap(mk=>effectiveModels(p,mk).flatMap(model=>submodelsForProductModel(p,mk,model)))));
    const liveSubs = allSubs.filter(v=>ruleAllowsSub(v,rule));
    const scope = {
      rule, lockedMake:null, lockedModel:null, lockedSub:null,
      startAt:rule?.startAt||'', multiBrand:liveMakes.length>1,
      singleMake:liveMakes.length===1?liveMakes[0]:null
    };
    // Collection scope is explicit maintained policy only. Never infer it from
    // title/handle/H1. The rule constrains both the visible hierarchy and Other.
    if(rule?.brands?.length===1){scope.lockedMake=canonical(allMakes,rule.brands[0]);scope.singleMake=scope.lockedMake;scope.multiBrand=false;}
    if(rule?.models?.length===1&&rule?.showModel!==true)scope.lockedModel=canonical(allModels,rule.models[0]);
    if(rule?.submodels?.length===1&&rule.keepSubmodelVisible!==true)scope.lockedSub=canonical(allSubs,rule.submodels[0]);
    return scope;
  }

  function isUniversalProduct(p) {
    return [...p.make, ...p.model, ...p.submodel].some(isUniversalValue);
  }
  function isSpecialOther(p) { return p.part_type.some(isSpecialPart); }
  function mappingHasSubmodel(make, model, sub) {
    const key=`${norm(make)}::${norm(model)}::${norm(sub)}`;
    if(mappingSubCache.has(key)) return mappingSubCache.get(key);
    const hit=Object.keys(FITMENT?.[make]?.[model] || {}).some(v => norm(v) === norm(sub) || contains(v, sub) || contains(sub, v));
    mappingSubCache.set(key,hit);
    return hit;
  }
  function productCache(p){
    if(p._ncCache)return p._ncCache;
    const cache={
      makeNorm:new Set(p.make.map(norm)), modelNorm:new Set(p.model.map(norm)), subNorm:new Set(p.submodel.map(norm)),
      yearNorm:new Set(p.year.map(norm)), partNorm:new Set(p.part_type.map(norm)), modelsByMake:new Map()
    };
    p._ncCache=cache; return cache;
  }
  function ownersForSub(sub){
    const key=norm(sub); if(subOwnerMap.has(key))return subOwnerMap.get(key);
    if(subOwnerFallbackCache.has(key))return subOwnerFallbackCache.get(key);
    const out=[];
    for(const [mk,models] of Object.entries(FITMENT)){
      if(isUniversalValue(mk))continue;
      for(const model of Object.keys(models||{}))if(mappingHasSubmodel(mk,model,sub))out.push({make:mk,model});
    }
    subOwnerFallbackCache.set(key,out); return out;
  }
  function effectiveMakes(p) {
    const c=productCache(p); if(c.effectiveMakes)return c.effectiveMakes;
    const branchMakes=uniq((p.fitment_branches||[]).map(b=>b.make).filter(v=>v&&!isUniversalValue(v)));
    if(branchMakes.length){c.effectiveMakes=branchMakes;return c.effectiveMakes;}
    const out = p.make.filter(v=>!isUniversalValue(v));
    if (!out.length) {
      for(const model of p.model.filter(v=>!isUniversalValue(v)))for(const owner of modelOwnerMap.get(norm(model))||[])out.push(owner.make);
      for(const sub of p.submodel.filter(v=>!isUniversalValue(v)))for(const owner of ownersForSub(sub))out.push(owner.make);
    }
    c.effectiveMakes=uniq(out); return c.effectiveMakes;
  }
  function modelKnownForMake(make, model) {
    const owners=modelOwnerMap.get(norm(model))||[];
    if(owners.some(o=>norm(o.make)===norm(make)))return true;
    return Object.keys(FITMENT?.[make]||{}).some(v=>norm(v)===norm(model));
  }
  function effectiveModels(p, make) {
    const c=productCache(p), k=norm(make); if(c.modelsByMake.has(k))return c.modelsByMake.get(k);
    const branchModels=uniq((p.fitment_branches||[]).filter(b=>norm(b.make)===k).map(b=>b.model).filter(v=>v&&!isUniversalValue(v)));
    if(branchModels.length){c.modelsByMake.set(k,branchModels);return branchModels;}
    const out=[];
    const explicit=p.model.filter(v=>!isUniversalValue(v));
    const pMakes=effectiveMakes(p);
    for(const model of explicit){
      const owners=modelOwnerMap.get(norm(model))||[];
      if(modelKnownForMake(make,model) || (!owners.length && pMakes.length===1 && norm(pMakes[0])===k))out.push(model);
    }
    // Fallback for legacy/incomplete products without fitment_key.
    for(const sub of p.submodel.filter(v=>!isUniversalValue(v)))for(const owner of ownersForSub(sub))if(norm(owner.make)===k)out.push(owner.model);
    const value=uniq(out); c.modelsByMake.set(k,value); return value;
  }
  function submodelsForProductModel(p, make, model) {
    const c=productCache(p); c.subsByModel ||= new Map();
    const key=`${norm(make)}::${norm(model)}`; if(c.subsByModel.has(key))return c.subsByModel.get(key);
    const branchSubs=uniq((p.fitment_branches||[]).filter(b=>norm(b.make)===norm(make)&&norm(b.model)===norm(model)).map(b=>b.sub).filter(v=>v&&!isUniversalValue(v)));
    if(branchSubs.length){c.subsByModel.set(key,branchSubs);return branchSubs;}
    const candidates=p.submodel.filter(v=>!isUniversalValue(v));
    const models=effectiveModels(p,make);
    const onlyModel=models.length===1 && norm(models[0])===norm(model);
    const out=[];
    for(const sub of candidates){
      const owners=ownersForSub(sub);
      const exactOwner=owners.some(o=>norm(o.make)===norm(make)&&norm(o.model)===norm(model));
      if(exactOwner || mappingHasSubmodel(make,model,sub)){out.push(sub);continue;}
      // Legacy fallback only when fitment_key is unavailable.
      if(!owners.length && (onlyModel || (norm(model).length>=2 && norm(sub).includes(norm(model)))))out.push(sub);
    }
    const value=uniq(out); c.subsByModel.set(key,value); return value;
  }

  function productMatchesMake(p, make) {
    const target=norm(make); return effectiveMakes(p).some(v=>norm(v)===target);
  }
  function productMatchesModel(p, make, model) {
    if (make && !productMatchesMake(p, make)) return false;
    const target=norm(model); return effectiveModels(p,make).some(v=>norm(v)===target);
  }
  function productMatchesSub(p, make, model, sub) {
    if(make && !productMatchesMake(p,make))return false;
    if(make && model){
      if(!productMatchesModel(p,make,model))return false;
      return submodelsForProductModel(p,make,model).some(v=>norm(v)===norm(sub)||contains(sub,v));
    }
    return p.submodel.some(v => norm(v) === norm(sub) || contains(sub, v));
  }

  function addDisplay(map,key,value){
    if(!map.has(key))map.set(key,new Map());
    map.get(key).set(norm(value),value);
  }
  function addId(map,key,id){if(!map.has(key))map.set(key,new Set());map.get(key).add(id);}
  function finalizeIndex(index){
    const makeMap=new Map(), modelsByMake=new Map(), subsByModel=new Map();
    const makeProductIds=new Map(),modelProductIds=new Map(),subProductIdsByModel=new Map();
    let universalCount=0;
    for(let pi=0;pi<index.products.length;pi++){
      const p=index.products[pi];
      productCache(p);
      p._ncUniversal=isUniversalProduct(p);
      p._ncSpecial=isSpecialOther(p);
      if(p._ncSpecial)continue;
      if(p._ncUniversal){universalCount++;continue;}
      for(const mk of effectiveMakes(p)){
        if(isUniversalValue(mk))continue;
        const mkn=norm(mk);makeMap.set(mkn,mk);addId(makeProductIds,mkn,pi);
        for(const model of effectiveModels(p,mk)){
          if(isUniversalValue(model))continue;
          addDisplay(modelsByMake,mkn,model);
          const token=`${mkn}::${norm(model)}`;addId(modelProductIds,token,pi);
          if(!subProductIdsByModel.has(token))subProductIdsByModel.set(token,new Map());
          for(const sub of submodelsForProductModel(p,mk,model)){
            addDisplay(subsByModel,token,sub);
            const sn=norm(sub),subMap=subProductIdsByModel.get(token);if(!subMap.has(sn))subMap.set(sn,new Set());subMap.get(sn).add(pi);
          }
        }
      }
    }
    index._liveMakes=[...makeMap.values()];
    index._liveModels=uniq([...modelsByMake.values()].flatMap(m=>[...m.values()]));
    index._liveSubs=uniq([...subsByModel.values()].flatMap(m=>[...m.values()]));
    index._modelsByMake=modelsByMake;
    index._subsByModel=subsByModel;
    index._makeProductIds=makeProductIds;
    index._modelProductIds=modelProductIds;
    index._subProductIdsByModel=subProductIdsByModel;
    index._allYears=uniq(index.products.flatMap(p=>p.year)).sort((a,b)=>isUniversalYear(a)?-1:isUniversalYear(b)?1:(Number(b)||0)-(Number(a)||0)||natural(a,b));
    index._allParts=uniq(index.products.flatMap(p=>p.part_type).filter(v=>!isSpecialPart(v))).sort(natural);
    index._universalCount=universalCount;
    index._fixedCounts={make:new Map(),model:new Map(),sub:new Map()};
    index._fixedScopeIds=new Map();
    index._metricsCache=new Map();
    index._vehicleVisibilityCache=new Map();
    index._resultCache=new Map();
    index._scopeCache=new Map();
    index._cardHtml=new Map();
    index._cardPagePromises=new Map();
    index._rankByHandle=new Map(index.products.map((p,i)=>[p.handle,i]));
    return index;
  }

  function productBelongsToScope(p, scope) {
    if(isUniversalProduct(p))return true;
    const rule=scope.rule;
    if(rule){
      const makes=(rule.brands?.length?rule.brands:(scope.lockedMake||scope.singleMake?[scope.lockedMake||scope.singleMake]:[]));
      const models=rule.models||[];
      const subs=rule.submodels||[];
      const branchRows=p.fitment_branches||[];
      if(branchRows.length){
        return branchRows.some(b=>{
          if(isUniversalValue(b.make)||isUniversalValue(b.model)||isUniversalValue(b.sub))return true;
          if(makes.length&&!makes.some(v=>norm(v)===norm(b.make)))return false;
          if(models.length&&!ruleAllowsModel(b.model,rule,b.make))return false;
          if(!ruleScopeAllowsSub(b.sub,rule))return false;
          if(subs.length&&rule.preservePolicySubmodels!==true&&!ruleScopeAllowsSub(b.sub,rule))return false;
          return true;
        });
      }
      const candidateMakes=makes.length?makes:(scope.lockedMake||scope.singleMake?[scope.lockedMake||scope.singleMake]:effectiveMakes(p));
      for(const mk of candidateMakes){
        if(!productMatchesMake(p,mk))continue;
        const candidateModels=models.length?models:effectiveModels(p,mk);
        for(const model of candidateModels){
          if(!ruleAllowsModel(model,rule,mk)||!productMatchesModel(p,mk,model))continue;
          const ps=submodelsForProductModel(p,mk,model);
          if(!ps.length&&!subs.length&&!Number.isFinite(rule.minNumber)&&!Number.isFinite(rule.maxNumber))return true;
          if(ps.some(sub=>ruleScopeAllowsSub(sub,rule)))return true;
        }
      }
      return false;
    }
    if(scope.lockedSub){
      const owners=Object.entries(FITMENT).flatMap(([mk,mm])=>Object.entries(mm||{}).filter(([,ss])=>Object.keys(ss||{}).some(s=>norm(s)===norm(scope.lockedSub))).map(([model])=>[mk,model]));
      return owners.some(([mk,model])=>productMatchesSub(p,mk,model,scope.lockedSub))||p.submodel.some(v=>norm(v)===norm(scope.lockedSub));
    }
    if(scope.lockedModel){const make=scope.lockedMake||scope.singleMake;return make?productMatchesModel(p,make,scope.lockedModel):p.model.some(v=>norm(v)===norm(scope.lockedModel));}
    if(scope.singleMake||scope.lockedMake)return productMatchesMake(p,scope.singleMake||scope.lockedMake);
    return [...p.make,...p.model,...p.submodel].some(v=>v&&!isUniversalValue(v));
  }
  function isOtherProduct(p, scope) { return isSpecialOther(p) || !productBelongsToScope(p, scope); }

  function urlValues(url, key) { return url.searchParams.getAll(key).filter(Boolean); }
  function parseModelToken(token) { const [make, ...rest] = String(token).split('::'); return {make, model:rest.join('::')}; }
  function parseSubToken(token) { const [make, model, ...rest] = String(token).split('::'); return {make, model, sub:rest.join('::')}; }
  function modelTokenKey(token){const x=typeof token==='string'?parseModelToken(token):token;return `${norm(x.make)}::${norm(x.model)}`;}
  function subTokenKey(token){const x=typeof token==='string'?parseSubToken(token):token;return `${norm(x.make)}::${norm(x.model)}::${norm(x.sub)}`;}
  function selected(url) {
    return {
      makes:urlValues(url,P.make), models:urlValues(url,P.model).map(parseModelToken), subs:urlValues(url,P.sub).map(parseSubToken),
      years:urlValues(url,P.year), parts:urlValues(url,P.part), universal:url.searchParams.get(P.universal)==='1', other:url.searchParams.get(P.other)==='1'
    };
  }
  function compileFilterState(url){
    const s=selected(url), values=url.searchParams.getAll(NATIVE_AVAIL);
    const rawMin=url.searchParams.get(PRICE_MIN),rawMax=url.searchParams.get(PRICE_MAX);
    const min=rawMin===null||rawMin===''?NaN:Number(rawMin),max=rawMax===null||rawMax===''?NaN:Number(rawMax);
    return {s,availability:values.length===1?values[0]:null,min,max,hasMin:Number.isFinite(min),hasMax:Number.isFinite(max)};
  }
  function availabilityMatchState(p,state){
    return state.availability==='1'?!!p.available:state.availability==='0'?!p.available:true;
  }
  function priceMatchState(p,state){
    if(!state.hasMin&&!state.hasMax)return true;
    const prices=p.prices.length?p.prices:[Number(p.price_min||0),Number(p.price_max||0)];
    return prices.some(raw=>{const v=Number(raw)/100;return (!state.hasMin||v>=state.min)&&(!state.hasMax||v<=state.max);});
  }

  function vehicleMatch(p, s) {
    if (s.universal) return isUniversalProduct(p);
    if (s.subs.length) return isUniversalProduct(p) || s.subs.some(x => productMatchesSub(p,x.make,x.model,x.sub));
    if (s.models.length) return isUniversalProduct(p) || s.models.some(x => productMatchesModel(p,x.make,x.model));
    if (s.makes.length) return isUniversalProduct(p) || s.makes.some(m => productMatchesMake(p,m));
    return true;
  }
  function yearMatch(p, years) {
    if (!years.length) return true;
    const specific = years.filter(v=>!isUniversalYear(v));
    if (!specific.length) return p.year.some(isUniversalYear);
    return p.year.some(v => isUniversalYear(v) || specific.some(y=>norm(y)===norm(v)));
  }
  function partMatch(p, parts) {
    if (!parts.length) return true;
    return p.part_type.some(v => !isSpecialPart(v) && parts.some(x=>norm(x)===norm(v)));
  }
  function matchProduct(p, url, scope, opts={}) {
    const state=opts.state||compileFilterState(url), s=state.s;
    if (s.other) return isOtherProduct(p, scope);
    const hasNormalFitment = s.universal || s.makes.length || s.models.length || s.subs.length || s.years.length || s.parts.length;
    if (hasNormalFitment && p._ncSpecial) return false;
    if (!vehicleMatch(p,s)) return false;
    if (!yearMatch(p,s.years)) return false;
    if (!partMatch(p,s.parts)) return false;
    if (!opts.ignoreAvailability && !availabilityMatchState(p,state)) return false;
    if (!opts.ignorePrice && !priceMatchState(p,state)) return false;
    return true;
  }

  function optionContext(root, index, url) {
    const h=null,s=selected(url),scopeKey=currentHandle(root)+'::'+[...(document.querySelectorAll('script[data-nicecnc-fitment-template],script[data-nicecnc-fitment-include]'))].map(node=>node.textContent).join('|');
    let scope=index._scopeCache.get(scopeKey);if(!scope){scope=inferScope(root,index,h);index._scopeCache.set(scopeKey,scope);}
    const rule=scope.rule;
    let makeValues=[...(index._liveMakes||[])].filter(v=>ruleAllowsMake(v,rule));
    if(scope.lockedSub||scope.lockedModel||scope.startAt==='submodel'||scope.startAt==='model'&&rule?.brands?.length===1)makeValues=[];
    else if(makeValues.length<=1&&rule?.showMake!==true)makeValues=[];
    makeValues.sort((a,b)=>(makePriority.get(a)??999)-(makePriority.get(b)??999)||natural(a,b));

    const makeScope=s.makes.length?s.makes:(scope.singleMake?[scope.singleMake]:scope.lockedMake?[scope.lockedMake]:[]);
    let modelTokens=[];
    if(scope.startAt!=='submodel'&&!scope.lockedSub){
      if(scope.lockedModel){
        const mk=scope.singleMake||scope.lockedMake||makeScope[0]||'';modelTokens=[{make:mk,model:scope.lockedModel}];
      }else{
        const makes=makeScope.length?makeScope:(makeValues.length&&!(scope.startAt==='model'&&rule?.keepSubmodelVisible===true)?[]:[...(index._liveMakes||[]).filter(v=>ruleAllowsMake(v,rule))]);
        for(const mk of makes){for(const model of index._modelsByMake.get(norm(mk))?.values()||[])if(ruleAllowsModel(model,rule,mk))modelTokens.push({make:mk,model});}
      }
    }else if(scope.startAt==='submodel'){
      if(rule?.models?.length){
        const mk=scope.singleMake||scope.lockedMake||rule.brands?.[0]||'';
        modelTokens=rule.modelPaths?.length?rule.modelPaths.map(parseModelToken):rule.models.map(model=>({make:mk,model}));
      }else{
        for(const mk of makeScope.length?makeScope:(index._liveMakes||[]).filter(v=>ruleAllowsMake(v,rule))){
          for(const model of index._modelsByMake.get(norm(mk))?.values()||[])if(ruleAllowsModel(model,rule,mk))modelTokens.push({make:mk,model});
        }
      }
    }
    modelTokens=modelTokens.filter(x=>ruleAllowsModel(x.model,rule,x.make));
    modelTokens.sort((a,b)=>natural(a.model,b.model)||natural(a.make,b.make));

    const effectiveModelTokens=s.models.length?s.models:(scope.lockedModel?modelTokens:modelTokens.length===1?modelTokens:(scope.startAt==='submodel'?modelTokens:[]));
    let subTokens=[];
    if(scope.lockedSub){
      const mk=scope.singleMake||scope.lockedMake||rule?.brands?.[0]||'';
      const model=scope.lockedModel||rule?.models?.[0]||'';subTokens=[{make:mk,model,sub:scope.lockedSub}];
    }else if(effectiveModelTokens.length){
      for(const x of effectiveModelTokens){
        const key=`${norm(x.make)}::${norm(x.model)}`;
        const values=[...(index._subsByModel.get(key)?.values()||[])];
        for(const sub of values)if(ruleAllowsSub(sub,rule))subTokens.push({...x,sub});
      }
      if(rule?.submodels?.length&&rule.preservePolicySubmodels!==true){
        subTokens=subTokens.filter(x=>ruleAllowsSub(x.sub,rule));
      }
    }
    // A locked Submodel collection can have the same canonical Submodel stored under
    // more than one historical Model (e.g. 1290 Super Adventure R/S under Adventure
    // and Super Adventure). Render one shopper-facing row while matching all real branches.
    if(scope.startAt==='submodel'&&rule?.models?.length>1){
      const grouped=new Map();
      for(const x of subTokens){const k=`${norm(x.make)}::${norm(x.sub)}`;if(!grouped.has(k))grouped.set(k,[]);grouped.get(k).push(x);}
      subTokens=[...grouped.values()].map(rows=>{
        const models=new Set(rows.map(x=>norm(x.model)).filter(Boolean));
        return models.size>1?{make:rows[0].make,model:'',sub:rows[0].sub}:rows[0];
      });
    }
    const seenSub=new Set();subTokens=subTokens.filter(x=>{const k=`${norm(x.make)}::${norm(x.model)}::${norm(x.sub)}`;if(seenSub.has(k))return false;seenSub.add(k);return true;});
    const priority=new Map((rule?.prioritySubmodels||[]).map((v,i)=>[norm(v),i]));
    subTokens.sort((a,b)=>{
      const pa=priority.has(norm(a.sub))?priority.get(norm(a.sub)):999,pb=priority.has(norm(b.sub))?priority.get(norm(b.sub)):999;if(pa!==pb)return pa-pb;
      if(rule?.numericFirst){const na=firstNumber(a.sub),nb=firstNumber(b.sub);if(Number.isFinite(na)&&Number.isFinite(nb)&&na!==nb)return na-nb;}
      return natural(a.sub,b.sub)||natural(a.model,b.model);
    });

    const vehicleReady=!!(s.universal||s.subs.length>0||scope.lockedSub||subTokens.length===1);
    return {h,scope,s,makeValues,modelTokens,subTokens,vehicleReady,allYears:index._allYears||[],allParts:index._allParts||[]};
  }

  function fixedScopeKey(scope){
    const r=scope?.rule||{};
    return JSON.stringify({b:r.brands||[],m:r.models||[],mp:r.modelPaths||[],s:r.submodels||[],a:r.allowedNumbers||[],tokens:r.allowedNumberTokens||[],min:r.minNumber??null,max:r.maxNumber??null,suf:r.submodelSuffix||'',closed:!!r.closedSubmodels,lm:scope?.lockedMake||'',lmo:scope?.lockedModel||'',ls:scope?.lockedSub||''});
  }
  function fixedScopeIds(index,scope){
    const key=fixedScopeKey(scope),cache=index._fixedScopeIds||(index._fixedScopeIds=new Map());
    if(cache.has(key))return cache.get(key);
    const ids=new Set();
    for(let i=0;i<index.products.length;i++){const p=index.products[i];if(p&&!p._ncSpecial&&productBelongsToScope(p,scope))ids.add(i);}
    cache.set(key,ids);return ids;
  }
  function fixedCount(index,type,value,scope){
    const bucket=index._fixedCounts?.[type];
    if(!bucket)return 0;
    const key=`${fixedScopeKey(scope)}::${String(value)}`;
    if(bucket.has(key))return bucket.get(key);
    const scopeIds=fixedScopeIds(index,scope),inScope=id=>scopeIds.has(id);
    let count=index._universalCount||0;
    if(type==='make'){
      for(const id of index._makeProductIds.get(norm(value))||[]) if(inScope(id)) count++;
    }else if(type==='model'){
      for(const id of index._modelProductIds.get(modelTokenKey(value))||[]) if(inScope(id)) count++;
    }else if(type==='sub'){
      const x=parseSubToken(value), ids=new Set();
      const addFromModel=modelKey=>{
        const displayMap=index._subsByModel.get(modelKey),idMap=index._subProductIdsByModel.get(modelKey);if(!displayMap||!idMap)return;
        for(const actual of displayMap.values())if(norm(actual)===norm(x.sub)||contains(x.sub,actual))for(const id of idMap.get(norm(actual))||[])if(inScope(id))ids.add(id);
      };
      if(x.make&&x.model)addFromModel(`${norm(x.make)}::${norm(x.model)}`);
      else if(x.make&&!x.model){const prefix=`${norm(x.make)}::`;for(const modelKey of index._subsByModel.keys())if(modelKey.startsWith(prefix))addFromModel(modelKey);}
      else if(x.model)addFromModel(`${norm(x.make)}::${norm(x.model)}`);
      else for(const modelKey of index._subsByModel.keys())addFromModel(modelKey);
      count+=ids.size;
    }
    bucket.set(key,count); return count;
  }


  function metricsKey(url,ctx){
    const s=selected(url);
    const sorted=a=>[...a].sort();
    return JSON.stringify({
      m:sorted(s.makes),mo:sorted(s.models.map(x=>`${x.make}::${x.model}`)),su:sorted(s.subs.map(x=>`${x.make}::${x.model}::${x.sub}`)),
      y:sorted(s.years),p:sorted(s.parts),u:s.universal?1:0,
      a:sorted(url.searchParams.getAll(NATIVE_AVAIL)),min:url.searchParams.get(PRICE_MIN)||'',max:url.searchParams.get(PRICE_MAX)||'',
      scope:[ctx.scope.lockedMake||'',ctx.scope.lockedModel||'',ctx.scope.lockedSub||'',ctx.scope.rule?.keepSubmodelVisible?fixedScopeKey(ctx.scope):'']
    });
  }
  function computeMetrics(index,root,url,ctx){
    const key=metricsKey(url,ctx), cache=index._metricsCache;
    if(cache?.has(key))return cache.get(key);
    const state=compileFilterState(url), s=ctx.s;
    const makeVisible=new Set(), modelVisible=new Set(), subVisible=new Set(), visibleExactSubsByModel=new Map();
    let universalLower=false;
    const lowerPass=p=>yearMatch(p,s.years)&&partMatch(p,s.parts)&&availabilityMatchState(p,state)&&priceMatchState(p,state);
    for(const p of index.products){
      if(p._ncSpecial||!lowerPass(p))continue;
      if(p._ncUniversal){universalLower=true;continue;}
      for(const mk of effectiveMakes(p)){
        makeVisible.add(norm(mk));
        for(const model of effectiveModels(p,mk)){
          const modelKey=modelTokenKey({make:mk,model});modelVisible.add(modelKey);
          if(!visibleExactSubsByModel.has(modelKey))visibleExactSubsByModel.set(modelKey,new Set());
          const set=visibleExactSubsByModel.get(modelKey);for(const sub of p.submodel)if(!isUniversalValue(sub))set.add(norm(sub));
        }
      }
    }
    const candidateVisibleInModel=(candidate,modelKey)=>{
      const exact=visibleExactSubsByModel.get(modelKey),display=index._subsByModel.get(modelKey);if(!exact||!display)return false;
      for(const actual of display.values())if(exact.has(norm(actual))&&(norm(actual)===norm(candidate)||contains(candidate,actual)))return true;
      return false;
    };
    for(const x of ctx.subTokens){
      if(x.make&&x.model){const mk=modelTokenKey({make:x.make,model:x.model});if(candidateVisibleInModel(x.sub,mk))subVisible.add(subTokenKey(x));}
      else if(x.make&&!x.model){const prefix=`${norm(x.make)}::`;for(const mk of visibleExactSubsByModel.keys())if(mk.startsWith(prefix)&&candidateVisibleInModel(x.sub,mk)){subVisible.add(subTokenKey(x));break;}}
      else{for(const mk of visibleExactSubsByModel.keys())if(candidateVisibleInModel(x.sub,mk)){subVisible.add(subTokenKey(x));break;}}
    }

    const yearDirect=new Map(), yearOverlap=new Map(), yearVisibleDirect=new Map(), yearVisibleOverlap=new Map();
    let uy=0, uyVisible=0;
    const partCounts=new Map(), partVisible=new Map();
    for(const p of index.products){
      if(p._ncSpecial||!vehicleMatch(p,s))continue;
      const avail=availabilityMatchState(p,state)&&priceMatchState(p,state);
      if(partMatch(p,s.parts)){
        const hasUY=p.year.some(isUniversalYear);
        if(hasUY){uy++;if(avail)uyVisible++;}
        const specific=uniq(p.year.filter(v=>!isUniversalYear(v)));
        for(const y of specific){
          const k=norm(y); yearDirect.set(k,(yearDirect.get(k)||0)+1);
          if(hasUY)yearOverlap.set(k,(yearOverlap.get(k)||0)+1);
          if(avail){yearVisibleDirect.set(k,(yearVisibleDirect.get(k)||0)+1);if(hasUY)yearVisibleOverlap.set(k,(yearVisibleOverlap.get(k)||0)+1);}
        }
      }
      if(yearMatch(p,s.years)){
        for(const part of uniq(p.part_type.filter(v=>!isSpecialPart(v)))){
          const k=norm(part); partCounts.set(k,(partCounts.get(k)||0)+1); if(avail)partVisible.set(k,(partVisible.get(k)||0)+1);
        }
      }
    }
    const yearCounts=new Map(), yearVisible=new Set();
    for(const y of ctx.allYears){
      const k=norm(y);
      if(isUniversalYear(y)){
        yearCounts.set(k,uy); if(uyVisible>0)yearVisible.add(k);
      }else{
        yearCounts.set(k,(yearDirect.get(k)||0)+uy-(yearOverlap.get(k)||0));
        if((yearVisibleDirect.get(k)||0)+uyVisible-(yearVisibleOverlap.get(k)||0)>0)yearVisible.add(k);
      }
    }
    const partVisibleSet=new Set([...partVisible.entries()].filter(([,n])=>n>0).map(([k])=>k));
    const out={makeVisible,modelVisible,subVisible,universalLower,yearCounts,yearVisible,partCounts,partVisible:partVisibleSet};
    if(cache){cache.set(key,out);if(cache.size>48)cache.delete(cache.keys().next().value);}
    return out;
  }

  function countCandidate(index, root, url, type, value, ctx) {
    if(type==='make'||type==='model'||type==='sub')return fixedCount(index,type,value,ctx?.scope);
    const metrics=computeMetrics(index,root,url,ctx);
    if(type==='year')return metrics.yearCounts.get(norm(value))||0;
    if(type==='part')return metrics.partCounts.get(norm(value))||0;
    return 0;
  }

  function group(root, param) { return root.querySelector(`[data-nicecnc-filter-param="${param}"]`); }
  function hideUnwantedNative(root){
    root.querySelectorAll('.product-facet__filter-item[data-nicecnc-filter-param]').forEach(g=>{
      const p=g.dataset.nicecncFilterParam||'';
      const custom=Object.values(P).includes(p);
      const keepNative=p===NATIVE_AVAIL||p.startsWith('filter.v.price');
      if(!custom&&!keepNative)g.hidden=true;
    });
  }
  function ensureGroup(root, param, label) {
    let g = group(root,param); if (g) return g;
    const list = root.querySelector('.product-facet__filter-list'); if (!list) return null;
    g = document.createElement('div'); g.className='product-facet__filter-item'; g.dataset.nicecncFilterParam=param;
    const id = `nc-filter-${param.replace(/[^a-z0-9]+/gi,'-')}`;
    g.innerHTML = `<button type="button" is="toggle-button" class="collapsible-toggle text--strong" aria-controls="${id}" aria-expanded="false">${label}<span aria-hidden="true">⌄</span></button><collapsible-content id="${id}" class="collapsible"><div class="collapsible__content"></div></collapsible-content>`;
    if(root._ncOpenGroups?.has(param)){g.querySelector('.collapsible-toggle')?.setAttribute('aria-expanded','true');g.querySelector('collapsible-content')?.setAttribute('open','');}
    list.append(g); return g;
  }
  function setGroupDisabled(g, disabled) {
    if (!g) return; g.dataset.nicecncDisabled = disabled?'1':'0';
    const b=g.querySelector('.collapsible-toggle'); if(b){b.disabled=!!disabled;b.setAttribute('aria-disabled',disabled?'true':'false');if(disabled){b.setAttribute('aria-expanded','false');g.querySelector('collapsible-content')?.removeAttribute('open');}}
    g.classList.toggle('nicecnc-filter-disabled',!!disabled);
  }
  function addSearch(content, options) {
    if (options.length <= 10) return;
    const box=document.createElement('input'); box.type='search'; box.className='nicecnc-filter-search'; box.placeholder='Search'; box.setAttribute('aria-label','Search filter options');
    const apply=()=>{
      const q=words(box.value);
      content.querySelectorAll('.checkbox-container').forEach(row=>{
        const miss=!!q&&!words(row.dataset.label||'').includes(q);
        row.classList.toggle('nicecnc-search-hidden',miss);
        row.setAttribute('aria-hidden',miss?'true':'false');
      });
    };
    box.addEventListener('input',apply);
    box.addEventListener('search',apply);
    content.append(box);
  }
  function renderRows(g, options, selectedValues, onChange, disabled=false) {
    const content=g.querySelector('.collapsible__content'); if(!content)return;
    content.replaceChildren(); addSearch(content,options);
    options.forEach((o,i)=>{
      const row=document.createElement('div'); row.className='checkbox-container'; row.dataset.label=o.label;
      const input=document.createElement('input'); input.type='checkbox'; input.className='checkbox nicecnc-fitment-input'; input.id=`nc-${g.dataset.nicecncFilterParam.replace(/\W+/g,'-')}-${i}`; input.value=o.value; input.checked=selectedValues.some(v=>v===o.value); input.disabled=disabled;
      const label=document.createElement('label'); label.htmlFor=input.id; label.textContent=`${o.label} (${o.count})`;
      input.addEventListener('change',()=>onChange(input,o)); row.append(input,label); content.append(row);
    });
  }
  function selectedSet(url,key){return new Set(url.searchParams.getAll(key));}
  function updateParam(url,key,value,checked){const vals=url.searchParams.getAll(key);url.searchParams.delete(key);const s=new Set(vals);checked?s.add(value):s.delete(value);[...s].forEach(v=>url.searchParams.append(key,v));url.searchParams.delete('page');}
  function dispatch(root,url){root.dispatchEvent(new CustomEvent('facet:criteria-changed',{bubbles:true,detail:{url:url.toString()}}));}

  function removeValues(url,key,predicate){const vals=url.searchParams.getAll(key);url.searchParams.delete(key);vals.filter(v=>!predicate(v)).forEach(v=>url.searchParams.append(key,v));}
  async function finishVehicleChange(index,root,url){url.searchParams.delete(P.universal);url.searchParams.delete(P.other);url.searchParams.delete('page');await sanitizeDownstream(index,root,url);dispatch(root,url);}

  function ensureButtons(root, index, ctx, url) {
    root.querySelectorAll('[data-nicecnc-standalone]').forEach(n=>n.remove());
    const list=root.querySelector('.product-facet__filter-list'); if(!list)return;
    const sub=group(root,P.sub), year=group(root,P.year);
    const universalCount=index._universalCount||0;
    index._otherCounts ||= new Map();
    const otherKey=`${ctx.scope.lockedMake||''}|${ctx.scope.lockedModel||''}|${ctx.scope.lockedSub||''}`;
    let otherCount=index._otherCounts.get(otherKey);
    if(otherCount==null){otherCount=index.products.filter(p=>isOtherProduct(p,ctx.scope)).length;index._otherCounts.set(otherKey,otherCount);}
    const makeButton=(kind,label,count,disabled,click)=>{const wrap=document.createElement('div');wrap.className='product-facet__filter-item nicecnc-standalone-filter';wrap.dataset.nicecncStandalone=kind;const b=document.createElement('button');b.type='button';b.className='nicecnc-standalone-button text--strong';b.disabled=disabled;b.textContent=`${label} (${count})`;if((kind==='universal'&&ctx.s.universal)||(kind==='other'&&ctx.s.other))b.classList.add('is-active');b.addEventListener('click',click);wrap.append(b);return wrap;};
    const ub=makeButton('universal','Universal',universalCount,universalCount===0,()=>{const u=new URL(location.href);[P.make,P.model,P.sub,P.other].forEach(k=>u.searchParams.delete(k));u.searchParams.set(P.universal,'1');u.searchParams.delete('page');sanitizeDownstream(index,root,u).then(()=>dispatch(root,u));});
    if(year) list.insertBefore(ub,year); else if(sub?.nextSibling) list.insertBefore(ub,sub.nextSibling); else list.append(ub);
    const ob=makeButton('other','Other',otherCount,otherCount===0,()=>{const u=new URL(location.href);Object.values(P).forEach(k=>u.searchParams.delete(k));[...u.searchParams.keys()].filter(k=>k.startsWith('filter.')).forEach(k=>u.searchParams.delete(k));u.searchParams.set(P.other,'1');u.searchParams.delete('page');dispatch(root,u);});
    list.append(ob);
  }

  function reorder(root) {
    const list=root.querySelector('.product-facet__filter-list'); if(!list)return;
    const order=[P.make,P.model,P.sub,'standalone:universal',P.year,P.part,NATIVE_AVAIL,'filter.v.price','standalone:other'];
    const nodes=[...list.children];
    const key=n=>n.dataset.nicecncStandalone?`standalone:${n.dataset.nicecncStandalone}`:(n.dataset.nicecncFilterParam||'');
    nodes.sort((a,b)=>{const ka=key(a),kb=key(b);const ia=order.findIndex(x=>ka===x||ka.startsWith(x)),ib=order.findIndex(x=>kb===x||kb.startsWith(x));return (ia<0?999:ia)-(ib<0?999:ib);});
    nodes.forEach(n=>list.append(n));
  }

  function hasNativeConstraint(url){
    return url.searchParams.has(NATIVE_AVAIL)||url.searchParams.has(PRICE_MIN)||url.searchParams.has(PRICE_MAX);
  }
  function hasLowerConstraint(url){
    return url.searchParams.has(P.year)||url.searchParams.has(P.part)||hasNativeConstraint(url);
  }
  function emptyMetrics(){
    return {makeVisible:new Set(),modelVisible:new Set(),subVisible:new Set(),universalLower:true,yearCounts:new Map(),yearVisible:new Set(),partCounts:new Map(),partVisible:new Set()};
  }
  function shellNativeValues(root,param){
    const native=root.querySelector(`[data-nicecnc-filter-param="${param}"]`); if(!native)return [];
    return uniq([...native.querySelectorAll(`input[name="${param}"]`)].map(x=>x.value).filter(v=>v&&!isUniversalValue(v)));
  }
  function prepareShellState(root){
    const rule=overrideFor(root);
    const makes=shellNativeValues(root,'filter.p.m.custom.vehicle_make').filter(v=>ruleAllowsMake(v,rule));
    const models=shellNativeValues(root,'filter.p.m.custom.vehicle_model').filter(v=>ruleAllowsModel(v,rule));
    const subs=shellNativeValues(root,'filter.p.m.custom.vehicle_submodel').filter(v=>ruleAllowsSub(v,rule));
    const gMake=ensureGroup(root,P.make,'Make'),gModel=ensureGroup(root,P.model,'Model'),gSub=ensureGroup(root,P.sub,'Submodel'),gYear=ensureGroup(root,P.year,'Year'),gPart=ensureGroup(root,P.part,'Part Type');
    const lockedSub=!!(rule?.submodels?.length===1&&rule.keepSubmodelVisible!==true),lockedModel=!!(rule?.models?.length===1&&rule?.showModel!==true);
    const startAt=rule?.startAt||'',multiBrand=rule?.brands?.length?rule.brands.length>1:makes.length>1;
    if(gMake)gMake.hidden=startAt==='model'||startAt==='submodel'||(!multiBrand&&rule?.showMake!==true)||lockedModel||lockedSub;
    if(gModel)gModel.hidden=startAt==='submodel'||lockedSub;
    if(gSub)gSub.hidden=false;
    const modelReady=startAt==='submodel'||lockedModel||models.length===1||(!multiBrand&&models.length===1);
    const subReady=lockedSub||subs.length===1;
    setGroupDisabled(gModel,(multiBrand&&!lockedModel)&&startAt!=='submodel');
    setGroupDisabled(gSub,!(lockedSub||modelReady));
    setGroupDisabled(gYear,!subReady);setGroupDisabled(gPart,!subReady);
    hideUnwantedNative(root);reorder(root);
  }

  async function hydrate(root) {
    if (!root || !location.pathname.includes('/collections/')) return;
    const token=(root._ncHydrateToken||0)+1; root._ncHydrateToken=token;
    const index=await loadIndex(location.href); if(root._ncHydrateToken!==token)return;
    const url=new URL(location.href), ctx=optionContext(root,index,url), s=ctx.s;

    // Vehicle rows have fixed counts. Only run the expensive Collection-wide dynamic
    // metrics pass after the vehicle path is ready (Year/Part) or when a native
    // Availability/Price/Year/Part constraint requires zero-result visibility checks.
    const needMetrics=ctx.vehicleReady||hasLowerConstraint(url);
    const metrics=needMetrics?computeMetrics(index,root,url,ctx):emptyMetrics();
    stateCache.set(root,{index,ctx,metrics});

    const gMake=ensureGroup(root,P.make,'Make'), gModel=ensureGroup(root,P.model,'Model'), gSub=ensureGroup(root,P.sub,'Submodel'), gYear=ensureGroup(root,P.year,'Year'), gPart=ensureGroup(root,P.part,'Part Type');
    ['filter.p.m.custom.vehicle_make','filter.p.m.custom.vehicle_model','filter.p.m.custom.vehicle_submodel','filter.p.m.custom.by_year','filter.p.m.custom.part_type'].forEach(param=>{const g=group(root,param);if(g)g.hidden=true;});

    const makeVisible=ctx.makeValues.length>0 && !ctx.scope.lockedModel && !ctx.scope.lockedSub && ctx.scope.startAt!=='model' && ctx.scope.startAt!=='submodel';
    gMake.hidden=!makeVisible;
    const modelVisible=!ctx.scope.lockedSub && ctx.scope.startAt!=='submodel';
    gModel.hidden=!modelVisible; gSub.hidden=false;

    const makeReady=!makeVisible || s.makes.length>0 || ctx.makeValues.length===1;
    const modelReady=!modelVisible || s.models.length>0 || ctx.modelTokens.length===1 || !!ctx.scope.lockedModel;
    setGroupDisabled(gModel,!makeReady && modelVisible);
    setGroupDisabled(gSub,!(makeReady&&modelReady) && !ctx.scope.lockedSub);
    setGroupDisabled(gYear,!ctx.vehicleReady);
    setGroupDisabled(gPart,!ctx.vehicleReady);

    const dynamicVehicleVisibility=hasLowerConstraint(url);
    const mkSel=[...selectedSet(url,P.make)];
    const makeOpts=ctx.makeValues.map(v=>({value:v,label:v,count:fixedCount(index,'make',v,ctx.scope)})).filter(o=>!dynamicVehicleVisibility||metrics.universalLower||metrics.makeVisible.has(norm(o.value))||mkSel.includes(o.value));
    renderRows(gMake,makeOpts,mkSel,(input,o)=>{const u=new URL(location.href);updateParam(u,P.make,o.value,input.checked);if(!input.checked){removeValues(u,P.model,v=>parseModelToken(v).make===o.value);removeValues(u,P.sub,v=>parseSubToken(v).make===o.value);}finishVehicleChange(index,root,u);},!makeVisible);

    const modelSel=[...selectedSet(url,P.model)], modelDisabled=gModel.dataset.nicecncDisabled==='1';
    const duplicateNames=new Map();ctx.modelTokens.forEach(x=>duplicateNames.set(norm(x.model),(duplicateNames.get(norm(x.model))||0)+1));
    const modelOpts=modelDisabled?[]:ctx.modelTokens.map(x=>{const value=`${x.make}::${x.model}`;return{value,label:duplicateNames.get(norm(x.model))>1?`${x.model} — ${x.make}`:x.model,count:fixedCount(index,'model',value,ctx.scope)}}).filter(o=>!dynamicVehicleVisibility||metrics.universalLower||metrics.modelVisible.has(modelTokenKey(o.value))||modelSel.includes(o.value));
    renderRows(gModel,modelOpts,modelSel,(input,o)=>{const u=new URL(location.href);updateParam(u,P.model,o.value,input.checked);if(!input.checked){const x=parseModelToken(o.value);removeValues(u,P.sub,v=>{const y=parseSubToken(v);return y.make===x.make&&y.model===x.model;});}finishVehicleChange(index,root,u);},modelDisabled);

    const subSel=[...selectedSet(url,P.sub)], subDisabled=gSub.dataset.nicecncDisabled==='1';
    const subOpts=subDisabled?[]:ctx.subTokens.map(x=>{const value=`${x.make}::${x.model}::${x.sub}`;return{value,label:x.sub,count:fixedCount(index,'sub',value,ctx.scope)}}).filter(o=>!dynamicVehicleVisibility||metrics.universalLower||metrics.subVisible.has(subTokenKey(o.value))||subSel.includes(o.value));
    renderRows(gSub,subOpts,subSel,(input,o)=>{const u=new URL(location.href);updateParam(u,P.sub,o.value,input.checked);finishVehicleChange(index,root,u);},subDisabled);

    const yearSel=[...selectedSet(url,P.year)], yearDisabled=gYear.dataset.nicecncDisabled==='1';
    const yearOpts=yearDisabled?[]:ctx.allYears.map(v=>({value:v,label:v,count:metrics.yearCounts.get(norm(v))||0})).filter(o=>o.count>0).filter(o=>metrics.yearVisible.has(norm(o.value))||yearSel.includes(o.value));
    renderRows(gYear,yearOpts,yearSel,(input,o)=>{const u=new URL(location.href);u.searchParams.delete(P.other);updateParam(u,P.year,o.value,input.checked);dispatch(root,u);},yearDisabled);

    const partSel=[...selectedSet(url,P.part)], partDisabled=gPart.dataset.nicecncDisabled==='1';
    const partOpts=partDisabled?[]:ctx.allParts.map(v=>({value:v,label:v,count:metrics.partCounts.get(norm(v))||0})).filter(o=>o.count>0).filter(o=>metrics.partVisible.has(norm(o.value))||partSel.includes(o.value));
    renderRows(gPart,partOpts,partSel,(input,o)=>{const u=new URL(location.href);u.searchParams.delete(P.other);updateParam(u,P.part,o.value,input.checked);dispatch(root,u);},partDisabled);

    ensureButtons(root,index,ctx,url); hideUnwantedNative(root); reorder(root); stripAvailabilityCounts(root); renderCustomActiveTags(root,url);
  }

  function stripAvailabilityCounts(root){
    root.querySelectorAll(`[data-nicecnc-filter-param="${NATIVE_AVAIL}"] label`).forEach(l=>{l.textContent=l.textContent.replace(/\s*\(\d+\)\s*$/,'');});
  }
  function renderCustomActiveTags(root,url){
    // Custom fitment chips were redundant (the checked rows already show state) and
    // created a confusing/occasionally blank “× Model:” tag under Filters. Keep native
    // Availability/Price chips, the filter badge, and one global Clear all only.
    root.querySelectorAll('[data-nicecnc-custom-tag]').forEach(n=>n.remove());
    const s=selected(url);
    const customCount=s.makes.length+s.models.length+s.subs.length+s.years.length+s.parts.length+(s.universal?1:0)+(s.other?1:0);
    const list=root.querySelector('.product-facet__active-list');
    const nativeCount=list?list.querySelectorAll('.tag:not([data-nicecnc-custom-tag])').length:0;
    const total=customCount+nativeCount;
    const toolbar=document.querySelector('.mobile-toolbar__item--filters'); if(toolbar)toolbar.classList.toggle('has-filters',total>0);
    const title=root.querySelector('.drawer__title');
    if(title){
      let bubble=title.querySelector('.product-facet__active-count');
      if(total>0&&!bubble){bubble=document.createElement('span');bubble.className='product-facet__active-count bubble-count';title.append(' ',bubble);}
      if(bubble){bubble.textContent=String(total);bubble.hidden=total===0;}
    }
    const header=root.querySelector('.drawer__header');
    let clear=header?.querySelector('[data-nicecnc-global-clear="1"]');
    if(total>0&&!clear&&header){
      clear=document.createElement('a');clear.className='drawer__header-action link text--subdued';clear.dataset.action='clear-filters';clear.dataset.nicecncGlobalClear='1';clear.href=collectionBase(location.href).toString();clear.textContent='Clear all';
      header.insertBefore(clear,header.querySelector('.drawer__close-button'));
    }else if(total===0&&clear)clear.remove();
    if(list&&!list.querySelector('.tag'))list.remove();
  }

  async function sanitizeDownstream(index,root,url){
    const ctx=optionContext(root,index,url); const s=selected(url);
    if(!ctx.vehicleReady){url.searchParams.delete(P.year);url.searchParams.delete(P.part);return;}
    for(const key of [P.year,P.part]){
      const vals=url.searchParams.getAll(key);url.searchParams.delete(key);
      for(const v of vals){const type=key===P.year?'year':'part';if(countCandidate(index,root,url,type,v,ctx)>0)url.searchParams.append(key,v);}
    }
  }

  function mergeNativeUrl(input){
    const next=new URL(input,location.href), current=new URL(location.href);
    for(const key of [P.make,P.model,P.sub,P.year,P.part,P.universal]){next.searchParams.delete(key);current.searchParams.getAll(key).forEach(v=>next.searchParams.append(key,v));}
    // Any ordinary filter action exits Other mode.
    next.searchParams.delete(P.other); next.searchParams.delete('page');
    return next;
  }
  function prepareShell(root){
    // Read native facet values before hiding them so the first usable layer is clickable
    // without building the full Collection audit index.
    prepareShellState(root);
  }

  function hasCustom(url){return Object.values(P).some(k=>url.searchParams.has(k));}
  function shouldRender(input){return location.pathname.includes('/collections/') && hasCustom(new URL(input,location.href));}

  function nativeSignature(input){
    const u=new URL(input,location.href), pairs=[];
    for(const [k,v] of u.searchParams){if(k.startsWith('filter.')&&!Object.values(P).includes(k))pairs.push([k,v]);}
    const sort=u.searchParams.get('sort_by'); if(sort)pairs.push(['sort_by',sort]);
    pairs.sort((a,b)=>a[0].localeCompare(b[0])||a[1].localeCompare(b[1]));
    return JSON.stringify(pairs);
  }
  function handleFromCard(card){
    const a=card?.querySelector?.('a[href*="/products/"]'); if(!a)return '';
    try{return decodeURIComponent(new URL(a.getAttribute('href'),location.origin).pathname.split('/products/')[1]?.split('/')[0]||'');}catch{return '';}
  }
  function seedVisibleCards(index,scope=document){
    scope.querySelectorAll?.('#facet-main .product-list__inner > *').forEach(card=>{const handle=handleFromCard(card);if(handle&&!index._cardHtml.has(handle))index._cardHtml.set(handle,card.outerHTML);});
  }
  async function loadCardPage(index,page){
    if(index._cardPagePromises.has(page))return index._cardPagePromises.get(page);
    const promise=(async()=>{
      const u=new URL(index.base);u.searchParams.set('view','nicecnc-cards');u.searchParams.set('page',String(page));
      const doc=parse(await fetchText(u));
      for(const wrap of doc.querySelectorAll('[data-nicecnc-card-handle]')){
        const handle=wrap.getAttribute('data-nicecnc-card-handle'),card=wrap.firstElementChild;
        if(handle&&card)index._cardHtml.set(handle,card.outerHTML);
      }
    })();
    index._cardPagePromises.set(page,promise);
    promise.catch(()=>index._cardPagePromises.delete(page));
    return promise;
  }
  async function loadStandardCardPage(index,page,sid,pageSize){
    index._standardCardPages ||= new Map();const key=`${sid}:${pageSize}:${page}`;
    if(index._standardCardPages.has(key))return index._standardCardPages.get(key);
    const promise=(async()=>{const u=new URL(index.base);u.searchParams.set('section_id',sid);u.searchParams.set('page',String(page));const doc=parse(await fetchText(u));seedVisibleCards(index,doc);})();
    index._standardCardPages.set(key,promise);promise.catch(()=>index._standardCardPages.delete(key));return promise;
  }
  async function ensureCards(index,wanted,facet,sid,pageSize){
    seedVisibleCards(index,facet||document);
    const pages=new Set();
    for(const {p} of wanted){if(index._cardHtml.has(p.handle))continue;const rank=index._rankByHandle.get(p.handle);if(rank!=null)pages.add(Math.floor(rank/CARD_PAGE_SIZE)+1);}
    const pending=[...pages];
    if(pending.length){
      try{await Promise.all(Array.from({length:Math.min(CARD_CONCURRENCY,pending.length)},async()=>{while(pending.length)await loadCardPage(index,pending.shift());}));}catch(e){/* fallback below */}
    }
    const fallbackPages=new Set();
    for(const {p} of wanted){if(index._cardHtml.has(p.handle))continue;const rank=index._rankByHandle.get(p.handle);if(rank!=null)fallbackPages.add(Math.floor(rank/pageSize)+1);}
    const fallback=[...fallbackPages];
    await Promise.all(Array.from({length:Math.min(3,fallback.length)},async()=>{while(fallback.length)await loadStandardCardPage(index,fallback.shift(),sid,pageSize);}));
  }
  function cloneFacetDocument(facet){
    const doc=document.implementation.createHTMLDocument('');
    doc.body.append(facet.cloneNode(true));
    return doc;
  }

  async function render(input, facet){
    const url=new URL(input,location.href), index=await loadIndex(url), root=facet.querySelector('facet-filters')||document.querySelector('facet-filters');
    const ctx=optionContext(root,index,url), sid=facet.getAttribute('section-id'), filterState=compileFilterState(url);
    const resultKey=JSON.stringify({custom:metricsKey(url,ctx),other:ctx.s.other?1:0});
    let matched=index._resultCache.get(resultKey);
    if(!matched){
      matched=[];
      for(let rank=0;rank<index.products.length;rank++){const p=index.products[rank];if(matchProduct(p,url,ctx.scope,{state:filterState}))matched.push({p,rank});}
      index._resultCache.set(resultKey,matched);if(index._resultCache.size>64)index._resultCache.delete(index._resultCache.keys().next().value);
    }

    const targetNative=nativeSignature(url);
    let doc;
    if(facet._ncNativeKey===targetNative){
      doc=cloneFacetDocument(facet);
    }else{
      const skeletonUrl=new URL(url);Object.values(P).forEach(k=>skeletonUrl.searchParams.delete(k));skeletonUrl.searchParams.set('section_id',sid);skeletonUrl.searchParams.delete('page');
      doc=parse(await fetchText(skeletonUrl,{fresh:true}));
    }
    const main=doc.querySelector('#facet-main'); if(!main)throw new Error('Missing facet main');
    const pageSize=Number(main.dataset.nicecncPageSize)||24,pages=Math.max(1,Math.ceil(matched.length/pageSize)),page=Math.min(pages,Math.max(1,Number(url.searchParams.get('page'))||1));
    const wanted=matched.slice((page-1)*pageSize,page*pageSize);
    await ensureCards(index,wanted,facet,sid,pageSize);
    const inner=main.querySelector('.product-list__inner');
    if(inner){
      inner.replaceChildren();
      for(const {p} of wanted){
        const html=index._cardHtml.get(p.handle);if(!html)throw new Error('Missing product card '+p.handle);
        const t=doc.createElement('template');t.innerHTML=html.trim();const card=t.content.firstElementChild;if(card)inner.append(card);
      }
      if(!wanted.length){const e=doc.createElement('p');e.className='empty-state';e.textContent='No matching products.';inner.append(e);}
    }
    main.dataset.nicecncResultCount=String(matched.length);const count=main.querySelector('.product-facet__meta-bar-item--count');if(count)count.textContent=`${matched.length} ${matched.length===1?'product':'products'}`;
    main.querySelectorAll('page-pagination').forEach(n=>n.remove());
    if(pages>1){
      const pager=doc.createElement('page-pagination');pager.setAttribute('ajax','');pager.className='pagination';const nav=doc.createElement('nav');nav.className='pagination__nav';
      const nums=[...new Set([1,page-1,page,page+1,pages])].filter(n=>n>=1&&n<=pages).sort((a,b)=>a-b);
      nums.forEach((n,i)=>{if(i&&n-nums[i-1]>1)nav.append(doc.createTextNode(' … '));const a=doc.createElement(n===page?'span':'a');a.className='pagination__nav-item heading heading--small';a.textContent=String(n);if(n===page)a.setAttribute('aria-current','page');else{const u=new URL(url);u.searchParams.set('page',String(n));a.href=u.href;a.dataset.page=String(n);}nav.append(a);});
      pager.append(nav);main.append(pager);
    }
    return doc.documentElement.outerHTML;
  }

  function installClearAll(root){
    root.addEventListener('click',e=>{const a=e.target.closest('[data-action="clear-filters"]');if(!a||a.classList.contains('tag__icon'))return;if(!location.pathname.includes('/collections/'))return;const u=collectionBase(location.href);e.preventDefault();e.stopImmediatePropagation();dispatch(root,u);},true);
  }
  function queueHydrate(root, immediate=false){
    if(!root||root._ncHydrateQueued)return;
    root._ncHydrateQueued=true;
    const run=()=>{root._ncHydrateQueued=false;hydrate(root).catch(console.error);};
    if(immediate){queueMicrotask(run);return;}
    if('requestIdleCallback'in window)requestIdleCallback(run,{timeout:1800});else setTimeout(run,700);
  }
  function initRoot(root){
    if(root.dataset.nicecncEngineInit==='1')return;
    root.dataset.nicecncEngineInit='1';root._ncOpenGroups=new Set();
    root.addEventListener('click',e=>{
      const b=e.target.closest('.collapsible-toggle');const g=b?.closest('[data-nicecnc-filter-param]');
      if(b&&g&&Object.values(P).includes(g.dataset.nicecncFilterParam)){
        const content=g.querySelector('.collapsible__content');
        if(!stateCache.has(root)||!content?.querySelector('.checkbox-container, .nicecnc-filter-search')){
          if(content&&!content.textContent.trim())content.textContent='Loading…';
          queueHydrate(root,true);
        }
        setTimeout(()=>{const key=g.dataset.nicecncFilterParam;if(b.getAttribute('aria-expanded')==='true')root._ncOpenGroups.add(key);else root._ncOpenGroups.delete(key);},0);
      }
    });
    prepareShell(root);installClearAll(root);
  }
  function init(){
    document.querySelectorAll('facet-filters').forEach(initRoot);
    const facet=document.querySelector('product-facet');
    if(facet){
      facet._ncNativeKey=nativeSignature(location.href);
      if(!facet.dataset.nicecncEngineEvents){
        facet.dataset.nicecncEngineEvents='1';
        facet.addEventListener('nicecnc:facets-rendered',()=>{
          facet._ncNativeKey=nativeSignature(location.href);
          document.querySelectorAll('facet-filters').forEach(r=>{initRoot(r);queueHydrate(r,true);});
        });
      }
    }
    // Keep first paint/critical assets clean. A shopper opening a custom group hydrates
    // immediately; otherwise only prefetch the shared audit index after a quiet delay.
    const prewarm=()=>{
      // Large Collections (notably All Products) stay fully lazy so background audit
      // requests cannot compete with native pagination. Opening a custom group still
      // calls hydrate/loadIndex immediately.
      const visibleTotal=Number(document.querySelector('#facet-main')?.dataset?.nicecncResultCount)||0;
      if(visibleTotal>CARD_PAGE_SIZE*2)return;
      const run=()=>loadIndex(location.href).catch(()=>{});
      if('requestIdleCallback'in window)requestIdleCallback(run,{timeout:5000});else setTimeout(run,1200);
    };
    const schedule=()=>setTimeout(prewarm,3500);
    if(document.readyState==='complete')schedule();else window.addEventListener('load',schedule,{once:true});
  }

  if(window.Shopify?.designMode){
    document.addEventListener('shopify:section:load', e=>{
      if(!e.target?.querySelector('script[data-nicecnc-fitment-template]'))return;
      document.querySelectorAll('facet-filters').forEach(root=>queueHydrate(root,true));
    });
  }

  window.NICECNCFilterEngine={VERSION,loadIndex,hydrate,render,shouldRender,mergeNativeUrl};
  if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',init,{once:true});else init();
})();
